import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../../Config/axiosInstance";
import Button from "@mui/material/Button";
import { IconButton, Tooltip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import GridOnIcon from "@mui/icons-material/GridOn";
import InsightsIcon from "@mui/icons-material/Insights";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import SubprojectCodeEdit from "./SubprojectCodeEdit";
import SubprojectCodeDelete from "./SubprojectCodeDelete";
import "./SubProjectCodePage.css";

export default function SubProjectCodePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const projectIdFromQuery = query.get("project");
  const projectIdFromState = location.state?.projectCodeId;
  const projectCodeFromState = location.state?.projectCode;
  const projectStatusFromState = location.state?.status ?? "active";
  const projectId = projectIdFromState || projectIdFromQuery || null;
  const [rows, setRows] = useState([]);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSubproject, setSelectedSubproject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subprojectToDelete, setSubprojectToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // La montare, citim permisiunile din localStorage
  useEffect(() => {
    const requiredPath = "/sub_project_codes";

    // Citim obiectul de permisiuni salvat local
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    // Funcție helper pentru a extrage path-ul sigur din URL
    const safePath = (url) => {
      try {
        return url?.startsWith("/") ? url : new URL(url).pathname;
      } catch {
        return url || "";
      }
    };

    // Verificăm dacă utilizatorul are drept de editare
    setCanEdit(
      permissions.some(
        (page) =>
          safePath(page.url) === requiredPath && page.can_edit === true
      )
    );

    // Verificăm dacă utilizatorul are drept de ștergere
    setCanDelete(
      permissions.some(
        (page) =>
          safePath(page.url) === requiredPath && page.can_delete === true
      )
    );
  }, []);

  // Normalizează statusul ca să avem doar "active" sau "completed"
  const normalizeStatus = (status) => {
    const s = (status ?? "").toString().trim().toLowerCase();
    return s === "completed" ? "completed" : "active";
  };

  // Returnează iconița potrivită pentru status
  const getStatusIcon = (status) =>
    status === "completed" ? (
      <CheckCircleIcon className="status-icon" />
    ) : (
      <RadioButtonUncheckedIcon className="status-icon" />
    );

  // Returnează textul potrivit pentru status
  const getStatusText = (status) => (status === "completed" ? "Completed" : "Active");

  // Render pentru oportunități
  const renderOpportunities = (opportunityLabel) => {
    if (!opportunityLabel || opportunityLabel === "—") {
      return <span className="opportunities-value">—</span>;
    }
    return <span className="opportunities-value">{opportunityLabel}</span>;
  };

  // Încarcă detaliile proiectului părinte
  const fetchProjectDetails = useCallback(async () => {
    if (!projectId) return null;

    try {
      const response = await axiosInstance.get(`/code_registries/${projectId}/`);

      if (response.data) {
        setProjectDetails(response.data);
        return response.data;
      }
    } catch (error) {
      console.error(
        "Eroare la fetchProjectDetails (/code_registries/:id):",
        error?.response?.data || error?.message || error
      );
    }

    return null;
  }, [projectId]);

  // Când avem projectId, încărcăm detaliile proiectului
  useEffect(() => {
    if (projectId) fetchProjectDetails();
  }, [projectId, fetchProjectDetails]);

  // Încarcă lista subproject codes pentru proiectul curent
  const fetchSubProjects = useCallback(async () => {
    if (!projectId) {
      setRows([]);
      return;
    }

    setLoading(true);

    try {
      const subProjectsResponse = await axiosInstance.get(`/subprojectcodes/by_project/${projectId}`);

      // Mapăm datele venite din backend într-un format potrivit pentru UI
      const mapped = (subProjectsResponse.data || [])
        .map((item, idx) => {
          const departmentName = item.department_label || "—";
          const activityName = item.activity_label || "—";
          const clientName = item.client_label || "—";
          const opportunityLabel = item.opportunity_id_selected_label || "—";
          const itemStatus = normalizeStatus(
            item.status !== undefined && item.status !== null ? item.status : projectStatusFromState
          );

          return {
            id: item.id ?? `${projectId}-${idx}`,
            code: item.code || "N/A",
            description: item.description || "No description available",
            departament: departmentName,
            activity: activityName,
            client: clientName,
            opportunities: opportunityLabel,
            status: itemStatus,
            created_at: item.created_at ?? null,
            matrix: item.matrix ?? null,
            _raw: item,
          };
        })
        // Sortare descrescătoare după data creării
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      setRows(mapped);
    } catch (error) {
      console.error("Error fetching subprojects:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, projectStatusFromState]);

  // Încărcăm subproiectele când componenta se montează sau projectId se schimbă
  useEffect(() => {
    fetchSubProjects();
  }, [fetchSubProjects]);

  // Determinăm statusul proiectului părinte
  const parentProjectStatus = useMemo(() => {
    if (projectDetails?.status !== undefined && projectDetails?.status !== null) {
      return normalizeStatus(projectDetails.status);
    }
    return normalizeStatus(projectStatusFromState);
  }, [projectDetails, projectStatusFromState]);

  // Determinăm codul proiectului părinte
  const parentProjectCode = useMemo(() => {
    if (projectCodeFromState) return projectCodeFromState;
    if (projectDetails?.code) return projectDetails.code;
    if (projectDetails?.project_code) return projectDetails.project_code;
    return `#${projectId}`;
  }, [projectDetails, projectCodeFromState, projectId]);

  // Filtrăm lista după termenul de căutare
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;

    const s = searchTerm.toLowerCase();

    return rows.filter(
      (row) =>
        row.code?.toLowerCase().includes(s) ||
        row.description?.toLowerCase().includes(s) ||
        row.departament?.toLowerCase().includes(s) ||
        row.activity?.toLowerCase().includes(s) ||
        row.client?.toLowerCase().includes(s) ||
        row.opportunities?.toLowerCase().includes(s)
    );
  }, [rows, searchTerm]);

  // Statistici simple pentru afișare
  const stats = useMemo(() => ({ total: rows.length }), [rows]);

  // Extragem doar rândurile de pe pagina curentă
  const paginatedRows = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  // Numărul total de pagini
  const totalPages = Math.ceil(filteredRows.length / pageSize);

  // Deschide dialogul de editare
  const handleEditClick = async (subproject) => {
    if (!canEdit) return;

    try {
      // Încercăm să luăm datele complete ale subproiectului
      const res = await axiosInstance.get(`/subprojectcodes/${subproject.id}/`);
      setSelectedSubproject(res.data);
    } catch (error) {
      // Dacă nu reușește, folosim fallback din lista deja încărcată
      setSelectedSubproject(subproject?._raw || subproject);
    }

    setEditDialogOpen(true);
  };

  // Închide dialogul de edit și reîncarcă lista dacă s-a salvat ceva
  const handleEditClose = (saved) => {
    setEditDialogOpen(false);
    setSelectedSubproject(null);

    if (saved) fetchSubProjects();
  };

  // Salvează modificările din dialogul de edit
  const handleEditSave = async (payload) => {
    if (!selectedSubproject?.id) return;

    try {
      await axiosInstance.patch(`/subprojectcodes/${selectedSubproject.id}/`, payload);
      handleEditClose(true);
    } catch (error) {
      console.error("Error save:", error?.response?.data || error?.message || error);
      alert("Error save");
    }
  };

  // Deschide dialogul de delete
  const handleDeleteClick = (subproject) => {
    if (!canDelete) return;

    setSubprojectToDelete(subproject);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  // Închide dialogul de delete și resetează state-ul aferent
  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSubprojectToDelete(null);
    setDeleteError(null);
  };

  // Confirmă ștergerea subproiectului
  const handleDeleteConfirm = async () => {
    if (!subprojectToDelete?.id) return;

    try {
      await axiosInstance.delete(`/subprojectcodes/${subprojectToDelete.id}/`);

      // Închidem dialogul și reîncărcăm lista
      handleDeleteClose();
      fetchSubProjects();
    } catch (error) {
      console.error("Error deleting subproject:", error);

      // Extragem un mesaj cât mai util din răspuns
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data ||
        error?.message;

      const normalizedMessage =
        typeof errorMessage === "string" ? errorMessage.toLowerCase() : "";

      // Detectăm dacă eroarea este cauzată de dependențe / foreign key / protected
      const isDependencyError =
        error?.response?.status === 400 ||
        error?.response?.status === 409 ||
        normalizedMessage.includes("dependent") ||
        normalizedMessage.includes("constraint") ||
        normalizedMessage.includes("foreign key") ||
        normalizedMessage.includes("protected") ||
        normalizedMessage.includes("reference");

      if (isDependencyError) {
        setDeleteError(
          "Cannot delete this subproject code because it has dependencies in other records. Please remove or update those dependencies first."
        );
      } else {
        setDeleteError(
          "An error occurred while trying to delete the subproject code. Please try again."
        );
      }
    }
  };

  return (
    <div className="subproject-page">
      {/* Header pagină */}
      <div className="header-section">
        <div className="header-row title-row">
          <div className="title-with-back">
            {/* Buton back */}
            <button className="back-arrow-btn" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </button>

            <h1 className="page-title">SUBPROJECT CODES</h1>
          </div>

          {/* Card cu informațiile proiectului părinte */}
          {projectId && (
            <div className="project-info-card">
              <FolderOpenIcon className="project-icon" />
              <span className="project-text">Project: {parentProjectCode}</span>

              <div className={`status-badge-header ${parentProjectStatus}`}>
                {getStatusIcon(parentProjectStatus)}
                <span>{getStatusText(parentProjectStatus)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Conținut principal */}
      <div className="subprojects-container">
        <div className="header-row stats-search-row">
          {/* Căutare */}
          <div className="search-container">
            <div className="search-box">
              <SearchIcon className="search-icon" />
              <input
                type="text"
                placeholder="Search by code, department, activity, client, opportunities..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
                className="search-input"
              />

              {/* Buton de clear search */}
              {searchTerm && (
                <button className="search-clear" onClick={() => setSearchTerm("")}>
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Card statistic total */}
          {projectId && (
            <div className="total-codes-card">
              <div className="card-icon-wrapper">
                <FolderIcon className="card-icon" />
              </div>

              <div className="card-info">
                <span className="card-label">TOTAL SUBCODES</span>
                <span className="card-value">{stats.total}</span>
              </div>
            </div>
          )}
        </div>

        {/* Loading state */}
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading subproject codes...</p>
          </div>
        ) : paginatedRows.length > 0 ? (
          <>
            {/* Grid-ul cu carduri */}
            <div className="subprojects-grid">
              {paginatedRows.map((row, index) => (
                <div
                  key={row.id}
                  className="subproject-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Header card */}
                  <div className="card-header">
                    <div className="card-icon-wrapper">
                      <FolderIcon className="card-folder-icon" />
                    </div>

                    <h3 className="card-title">{row.code}</h3>

                    {/* Acțiuni edit/delete */}
                    {(canEdit || canDelete) && (
                      <div className="card-actions">
                        {canEdit && (
                          <Tooltip title="Edit Subproject">
                            <IconButton
                              onClick={() => handleEditClick(row)}
                              className="edit-card-button"
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {canDelete && (
                          <Tooltip title="Delete Subproject">
                            <IconButton
                              onClick={() => handleDeleteClick(row)}
                              size="small"
                              sx={{ color: "#d32f2f" }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Body card */}
                  <div className="card-body">
                    {/* Matrix details */}
                    <div className="matrix-section">
                      <div className="matrix-title">
                        <GridOnIcon />
                        <span>Matrix Details</span>
                      </div>

                      <div className="matrix-grid">
                        <div className="matrix-item">
                          <div className="matrix-item-label">
                            <BusinessIcon />
                            <span>Departament</span>
                          </div>
                          <div className="matrix-item-value">{row.departament}</div>
                        </div>

                        <div className="matrix-item">
                          <div className="matrix-item-label">
                            <InsightsIcon />
                            <span>Activity</span>
                          </div>
                          <div className="matrix-item-value">{row.activity}</div>
                        </div>
                      </div>
                    </div>

                    {/* Client */}
                    <div className="client-section">
                      <div className="client-header">
                        <BusinessIcon />
                        <span>Client</span>
                      </div>

                      <div className="client-content">
                        <div className="client-avatar">
                          <BusinessIcon />
                        </div>
                        <div className="client-details">
                          <div className="client-name">{row.client}</div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="description-section">
                      <div className="description-header">
                        <DescriptionIcon />
                        <span>Description</span>
                      </div>

                      <div className="description-content">
                        <div className="description-text">{row.description}</div>
                      </div>
                    </div>

                    {/* Opportunities */}
                    <div className="opportunities-section">
                      <div className="opportunities-header">
                        <InsightsIcon />
                        <span>Opportunities</span>
                      </div>

                      <div className="opportunities-content">
                        {renderOpportunities(row.opportunities)}
                      </div>
                    </div>
                  </div>

                  {/* Footer card */}
                  <div className="card-footer">
                    <div className={`status-badge ${row.status}`}>
                      {row.status === "completed" ? (
                        <CheckCircleIcon />
                      ) : (
                        <RadioButtonUncheckedIcon />
                      )}
                      <span>{row.status === "completed" ? "Completed" : "Active"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginare */}
            <div className="pagination-container">
              <div className="pagination-info">
                <span>
                  Showing {currentPage * pageSize + 1} to{" "}
                  {Math.min((currentPage + 1) * pageSize, filteredRows.length)} of{" "}
                  <strong>{filteredRows.length}</strong> entries
                </span>
              </div>

              <div className="pagination-controls">
                {/* Selector rows per page */}
                <div className="rows-per-page">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(0);
                    }}
                    className="rows-select"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                {/* Butoane paginare */}
                <div className="pagination-buttons">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    ‹
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      className={`pagination-btn ${currentPage === i ? "active" : ""}`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={totalPages === 0 || currentPage === totalPages - 1}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          // Empty state
          <div className="empty-state">
            <div className="empty-icon-wrapper">
              <FolderIcon className="empty-icon" />
            </div>

            <h3>No Subproject Codes Found</h3>
            <p>This project doesn't have any subproject codes yet.</p>

            {searchTerm && (
              <Button variant="text" className="clear-search-btn" onClick={() => setSearchTerm("")}>
                Clear search
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Dialog edit */}
      <SubprojectCodeEdit
        open={editDialogOpen}
        onClose={handleEditClose}
        subprojectData={selectedSubproject}
        projectId={projectId}
        onSave={handleEditSave}
      />

      {/* Dialog delete */}
      <SubprojectCodeDelete
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        subproject={subprojectToDelete}
        deleteError={deleteError}
      />
    </div>
  );
}